<template>
  <app-page
    title="Create a milestone award"
    subtitle="Set the basics, choose who can earn it, and personalise the award card."
    :previous="[{ label: 'Recognition', to: { name: 'recognition' } }]"
  >
    <template #below-header>
      <p v-if="shouldShowCompanyIndicator" class="company-note">
        This award will be created for <active-company-pill />
      </p>
    </template>

    <template #custom-content>
      <b-col>
        <ValidationObserver v-slot="v">
          <b-form id="create-award-form" @submit.prevent="v.handleSubmit(onSubmit)">
            <div class="form-container">
              <!-- Basic Details -->
              <basic-details-section :name="awardForm.name" @update:name="awardForm.name = $event">
                <template #details>
                  <div class="milestone-event-type-container">
                    <app-input
                      v-model="milestoneEventType"
                      :validate-immediate="false"
                      name="Milestone type"
                      type="dropdown"
                      :options="milestoneOptions"
                      undefined-label="Select type"
                      rules="required"
                    >
                      <template #label>
                        <div class="label">
                          Milestone type
                          <small>Choose which milestone you'd like to celebrate with this award.</small>
                        </div>
                      </template>
                    </app-input>
                  </div>

                  <app-input
                    v-if="milestoneEventType === RECOGNITION_MILESTONE_EVENT_TYPES.ANNIVERSARY"
                    v-model="anniversaryYear"
                    :validate-immediate="false"
                    name="Anniversary year"
                    type="number"
                    placeholder="e.g. 5"
                    append="years"
                    inputmode="numeric"
                    pattern="[0-9]*"
                    step="1"
                    :rules="
                      milestoneEventType === RECOGNITION_MILESTONE_EVENT_TYPES.ANNIVERSARY
                        ? 'required|min_value:1|max_value:50'
                        : ''
                    "
                    class="anniversary-year-input m-0"
                    @input="onAnniversaryYearInput"
                  >
                    <template #label>
                      <div class="label">
                        Anniversary year
                        <small>Which work anniversary should trigger this award?</small>
                      </div>
                    </template>
                  </app-input>

                  <app-input
                    v-model="recipientMessage"
                    :validate-immediate="false"
                    name="Recipient message"
                    type="textarea"
                    :rows="2"
                    :maxlength="1000"
                    rules="required|max:1000"
                    :description="recipientMessageDescription"
                    placeholder="e.g. Congratulation on your 5 year anniversary with the company!"
                    @input="onRecipientMessageInput"
                  >
                    <template #label>
                      <div class="label">
                        Add a message
                        <small>Write a default message that celebrates the milestone.</small>
                      </div>
                    </template>
                  </app-input>
                </template>
              </basic-details-section>

              <div>
                <h4>Who can receive?</h4>
                <sme-card large>
                  <div class="card-container">
                    <award-scope-fields
                      :scope="scope"
                      :scope-fields="scopeFields"
                      :scope-error="divisionScopeError"
                      @update:scope="onScopeChange"
                    />
                  </div>
                </sme-card>
              </div>

              <!-- Appearance -->
              <image-upload-section
                :banner-images="bannerImages"
                :banner-images-error="bannerImagesError"
                :badge-file="badgeFile"
                :badge-preview-url="badgePreviewUrl"
                :allowed-image-types="allowedImageTypes"
                :max-size-bytes="MAX_BANNER_SIZE_BYTES"
                @add:bannerImages="addBannerImages"
                @add:pendingBannerFiles="addPendingBannerFiles"
                @remove:bannerImage="removeBannerImage"
                @update:badgeFile="onBadgeFileUpdate"
                @update:badgePreviewUrl="onBadgePreviewUrlUpdate"
                @gallerySelect:badge="onBadgeGallerySelect"
              />

              <!-- Award Details -->
              <div>
                <h4>Award details</h4>
                <sme-card large>
                  <div class="card-container">
                    <div class="label-row">
                      <div class="label">
                        Enable badges (optional)
                        <small>Turn awards into collectible badges that appear on recipients' home page.</small>
                      </div>
                      <b-form-checkbox v-model="badgesEnabled" switch />
                    </div>

                    <monetary-reward-section
                      :enabled="monetaryRewardEnabled"
                      :currency-symbol="currencySymbol"
                      @update:enabled="monetaryRewardEnabled = $event"
                      :amounts="awardValueMinor"
                      :amount-error="awardValueError"
                      :redemption-options="selectedRedemptionOptions"
                      :redemption-error="redemptionSelectionError"
                      :expiration-months="expirationMonths"
                      @update:amounts="awardValueMinor = $event"
                      @update:redemptionOptions="selectedRedemptionOptions = $event"
                      @update:expirationMonths="expirationMonths = $event"
                    >
                      <template #reward-amounts>
                        <div class="reward-amount-container">
                          <app-input
                            v-model="singleAwardValue"
                            :validate-immediate="false"
                            name="Reward amount"
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                            inputmode="decimal"
                            :prepend="currencySymbol"
                            class="amount-input"
                            @input="onAmountInput($event)"
                            @keydown="onAmountKeydown"
                            @blur="onAmountBlur()"
                            @change="onAmountBlur()"
                          />
                        </div>
                      </template>
                    </monetary-reward-section>
                  </div>
                </sme-card>
              </div>
            </div>
          </b-form>
        </ValidationObserver>
      </b-col>
    </template>

    <template #footer>
      <div class="d-flex align-items-center justify-content-between w-100 px-4 py-3">
        <b-button variant="outline-secondary" :disabled="saving" @click="$router.back()">Cancel</b-button>
        <div class="d-flex align-items-center">
          <small v-if="errorMessage" class="text-danger mr-3">{{ errorMessage }}</small>
          <b-button type="submit" variant="primary" :disabled="saving" form="create-award-form">
            <b-spinner v-if="saving" class="mr-2" small />
            {{ saving ? 'Creating...' : 'Create award' }}
          </b-button>
        </div>
      </div>
    </template>
  </app-page>
</template>

<script setup>
import { ValidationObserver } from 'vee-validate';
import { onBeforeMount, ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router/composables';
import ApiClient, { apiErrorData } from '@/ApiClient';
import {
  RECOGNITION_AWARD_TYPES,
  RECOGNITION_APPROVAL_TYPES,
  RECOGNITION_MILESTONE_EVENT_OPTIONS,
  RECOGNITION_MILESTONE_EVENT_TYPES,
  toTwoDecimalsOrEmpty,
} from '@/pages/recognition/constants';
import { useAwardForm } from '@/pages/recognition/composables/useAwardForm';
import AppInput from '@/components/AppInput.vue';
import AppPage from '@/components/AppPage.vue';
import useToast from '@/composables/useToast';
import SmeCard from '@/components/atoms/SmeCard.vue';
import useCompanySwitcher from '@/state/composables/useCompanySwitcher';
import ActiveCompanyPill from '@/pages/recognition/components/ActiveCompanyPill.vue';
import AwardScopeFields from '@/pages/recognition/components/AwardScopeFields.vue';
import BasicDetailsSection from '@/pages/recognition/components/BasicDetailsSection.vue';
import ImageUploadSection from '@/pages/recognition/components/ImageUploadSection.vue';
import MonetaryRewardSection from '@/pages/recognition/components/MonetaryRewardSection.vue';

const { shouldShowCompanyIndicator } = useCompanySwitcher();

// Use shared composable
const {
  currencySymbol,
  awardForm,
  scope,
  scopeFields,
  programScopePayload,
  divisionScopeError,
  initDivisionScope,
  validateDivisionScope,
  monetaryRewardEnabled,
  awardValueMinor,
  awardValueError,
  selectedRedemptionOptions,
  redemptionSelectionError,
  expirationMonths,
  bannerImages,
  bannerImagesError,
  addBannerImages,
  addPendingBannerFiles,
  removeBannerImage,
  badgeFile,
  badgePreviewUrl,
  allowedImageTypes,
  MAX_BANNER_SIZE_BYTES,
  onBadgeFileUpdate,
  onBadgePreviewUrlUpdate,
  onBadgeGallerySelect,
  saving,
  errorMessage,
  sanitizeAmountInput,
  majorToMinor,
  onAmountKeydown,
  loadScopeFields,
  setupCommonWatchers,
  resetMonetaryRewardFields,
  getImageData,
  getBudgetMinor,
  getGrantExpiryInterval,
  validateRedemptionOptions,
} = useAwardForm();

// Start with the monetary toggle on by default; admins can flip it off to create
// a badge-only milestone (backend treats omitted/null award_value_minor accordingly).
monetaryRewardEnabled.value = true;

// Milestone-specific state
const badgesEnabled = ref(true);
const milestoneEventType = ref(undefined);
const milestoneOptions = RECOGNITION_MILESTONE_EVENT_OPTIONS;
const anniversaryYear = ref('');
const recipientMessage = ref('');
const singleAwardValue = ref(undefined);

const recipientMessageDescription = computed(() => {
  const len = String(recipientMessage.value || '').length;
  return `${len}/1000 characters`;
});

onBeforeMount(async () => {
  await loadScopeFields();
  initDivisionScope();
});

const $router = useRouter();
const { doToast, TOAST_VARIANT } = useToast();

// Setup common watchers
setupCommonWatchers();

// Milestone-specific helpers
const buildProperties = redemptionMethods => {
  // API requires at least 1 valid_grant_type - default to VOUCHER if none provided
  const grantTypes = (redemptionMethods || []).map(String);
  const props = {
    valid_grant_types: grantTypes.length > 0 ? grantTypes : ['VOUCHER'],
    approvers: undefined,
  };

  if (monetaryRewardEnabled.value) {
    const monthsInt = parseInt(String(expirationMonths.value || '').trim(), 10);
    if (Number.isFinite(monthsInt) && monthsInt >= 0) {
      props.expiration_months = monthsInt;
    }
  }

  if (monetaryRewardEnabled.value && singleAwardValue.value !== undefined && singleAwardValue.value !== null) {
    const minor = majorToMinor(singleAwardValue.value);
    if (Number.isFinite(minor)) {
      props.award_value_minor = minor;
      props.default_award_value_minor = minor;
    }
  }

  if (milestoneEventType.value) {
    const eventType = String(milestoneEventType.value);
    const milestone = { event_type: eventType };
    if (eventType === RECOGNITION_MILESTONE_EVENT_TYPES.ANNIVERSARY) {
      const yearInt = parseInt(String(anniversaryYear.value || '').trim(), 10);
      if (Number.isFinite(yearInt)) {
        milestone.anniversary_year = yearInt;
      }
    }
    milestone.recipient_message = String(recipientMessage.value || '').slice(0, 1000);
    props.milestone = milestone;
  }

  return props;
};

const buildPayload = properties => ({
  program_type: RECOGNITION_AWARD_TYPES.MILESTONE,
  program_name: awardForm.value.name,
  approval_type: RECOGNITION_APPROVAL_TYPES.NONE,
  show_badge: badgesEnabled.value,
  ...programScopePayload.value,
  budget_minor: getBudgetMinor(),
  grant_expiry_interval: getGrantExpiryInterval(),
  properties,
});

const onSubmit = async () => {
  errorMessage.value = '';
  saving.value = true;
  const redemptionMethods = monetaryRewardEnabled.value ? selectedRedemptionOptions.value || [] : [];

  try {
    if (!validateDivisionScope()) {
      saving.value = false;
      return;
    }

    if (!validateRedemptionOptions(redemptionMethods)) {
      saving.value = false;
      return;
    }

    // Require a valid single award value when monetary rewards are enabled
    if (monetaryRewardEnabled.value) {
      const minor = majorToMinor(singleAwardValue.value);
      if (!Number.isFinite(minor) || minor <= 0) {
        awardValueError.value = 'Please enter a valid reward amount.';
        saving.value = false;
        return;
      }
      awardValueError.value = '';
    }

    const properties = buildProperties(redemptionMethods);
    const payload = buildPayload(properties);
    const { files, s3Keys } = getImageData();

    await ApiClient.createRecognitionProgram(payload, files, s3Keys);

    doToast('Success', 'Milestone award created', TOAST_VARIANT.SUCCESS);
    $router.push({ name: 'recognition' });
  } catch (e) {
    errorMessage.value = apiErrorData(e).message || e?.message || 'Failed to create award';
  } finally {
    saving.value = false;
  }
};

const onScopeChange = newScope => {
  scope.value = newScope;
};

const onAmountInput = value => {
  singleAwardValue.value = sanitizeAmountInput(value);
};

const onAmountBlur = () => {
  singleAwardValue.value =
    singleAwardValue.value === '' || singleAwardValue.value === undefined
      ? ''
      : toTwoDecimalsOrEmpty(singleAwardValue.value);
};

const onAnniversaryYearInput = value => {
  const cleaned = String(value ?? '').replace(/[^0-9]/g, '');
  anniversaryYear.value = cleaned;
};

const onRecipientMessageInput = value => {
  recipientMessage.value = String(value ?? '').slice(0, 1000);
};

watch(milestoneEventType, val => {
  if (val !== RECOGNITION_MILESTONE_EVENT_TYPES.ANNIVERSARY) {
    anniversaryYear.value = '';
  }
});

watch(singleAwardValue, newVal => {
  if (!monetaryRewardEnabled.value) {
    awardValueError.value = '';
    return;
  }
  const minor = majorToMinor(newVal);
  if (Number.isFinite(minor) && awardValueError.value) {
    awardValueError.value = '';
  }
});

watch(monetaryRewardEnabled, enabled => {
  if (!enabled) {
    resetMonetaryRewardFields();
    singleAwardValue.value = undefined;
  }
});
</script>

<style scoped lang="scss">
@import '@/pages/recognition/styles/award-form';
</style>
